import { isArray } from 'lodash-es';

const COLOR_KEYS = [
	'VIOLET',
	'COBALT',
	'EMERALD',
	'RUST',
	'OLIVE',
	'ORANGE',
	'PINK',
	'TAN',
	'PURPLE',
	'CYAN',
] as const;

type TColorKeys = (typeof COLOR_KEYS)[number];

const length = COLOR_KEYS.length;
const COLOR_PRIORITY_MAP = Object.values(COLOR_KEYS).reduce(
	(prev, key, index) => {
		return Object.assign(prev, {
			[key]: length - index,
		});
	},
	{} as Record<string, number>,
);

export type IColorName = keyof typeof COLOR_NAME_MAP;

type IFlowColorData = keyof typeof COLOR_PRIORITY_MAP;

export const COLOR_NAME_MAP = {
	VIOLET: {
		light: 'rgba(234, 230, 255, 1)',
		dark: 'rgba(112, 82, 247, 1)',
	},
	COBALT: {
		light: 'rgba(226, 239, 253, 1)',
		dark: 'rgba(13, 113, 219, 1)',
	},
	EMERALD: {
		light: 'rgba(217, 246, 233, 1)',
		dark: 'rgba(19, 168, 101, 1)',
	},
	RUST: {
		light: 'rgba(255, 228, 217, 1)',
		dark: 'rgba(212, 86, 36, 1)',
	},
	OLIVE: {
		light: 'rgba(231, 243, 208, 1)',
		dark: 'rgba(107, 156, 17, 1)',
	},
	ORANGE: {
		light: 'rgba(253, 233, 206, 1)',
		dark: 'rgba(217, 127, 9, 1)',
	},
	PINK: {
		light: 'rgba(252, 218, 242, 1)',
		dark: 'rgba(222, 38, 167, 1)',
	},
	TAN: {
		light: 'rgba(238, 232, 208, 1)',
		dark: 'rgba(171, 139, 10, 1)',
	},
	PURPLE: {
		light: 'rgba(244, 217, 255, 1)',
		dark: 'rgba(179, 57, 227, 1)',
	},
	CYAN: {
		light: 'rgba(209, 255, 255, 1)',
		dark: 'rgba(11, 158, 158, 1)',
	},
} as Record<TColorKeys, { light: string; dark: string }>;

export const DEFAULT_COLOR = {
	light: 'rgba(234, 236, 240, 1)',
	dark: 'rgba(152, 162, 179, 1)',
};

export class BuildColorManger {
	private map: Map<string, number>;
	private top: IFlowColorData;

	constructor(data: IFlowColorData[]) {
		this.map = new Map();
		this.top = COLOR_KEYS[0];
		this.reset(data);
	}

	public reset(data: IFlowColorData[]) {
		this.map.clear();
		data.forEach((color) => this.mapOp(true, color));

		this.resort();
	}

	private resort() {
		const map = this.map;

		let top: string = COLOR_KEYS[0];

		COLOR_KEYS.forEach((color, index) => {
			if (index === 0) return;

			const prev = map.get(top) ?? 0;
			const cur = map.get(color) ?? 0;

			if (prev === cur) {
				top = COLOR_PRIORITY_MAP[top] > COLOR_PRIORITY_MAP[color] ? top : color;
				return;
			}

			top = prev > cur ? color : top;
		});

		this.top = top;
	}

	private mapOp(add: boolean, color: IFlowColorData) {
		const map = this.map!;
		const value = add ? (map.get(color) ?? 0) + 1 : Math.max((map.get(color) ?? 0) - 1, 0);
		map.set(color, value);
	}

	private applyColorChanges(type: 'add' | 'remove', changes: IFlowColorData[]) {
		changes.forEach((color) => {
			this.mapOp(type === 'add', color);
		});

		this.resort();
	}

	peek() {
		return this.top;
	}

	addColor() {
		const color = this.peek();

		this.applyColorChanges('add', [color]);

		return color;
	}

	removeColors(data?: IFlowColorData | IFlowColorData[]) {
		if (!data) return;
		this.applyColorChanges('remove', isArray(data) ? data : [data]);
	}
}

export const flowColorManager = new BuildColorManger([]);

export const getColor = (colorName?: string, type: 'light' | 'dark' = 'light') => {
	let ret = DEFAULT_COLOR[type];

	if (colorName && COLOR_NAME_MAP[colorName as TColorKeys]) {
		ret = COLOR_NAME_MAP[colorName as TColorKeys][type];
	}

	return ret || DEFAULT_COLOR['light'];
};

export const modifyColorAlpha = (color: string, newAlpha: number) => {
	const empty = 'rgba(255, 255, 255, 0)';
	const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
	if (match) {
		return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${newAlpha})`;
	}
	return empty;
};

export const transformOldColor = (color: string) => {
	if (COLOR_NAME_MAP[color as TColorKeys]) {
		return color;
	}

	return (
		{
			sky: 'VIOLET',
			orange: 'COBALT',
			amber: 'EMERALD',
			green: 'RUST',
			pink: 'OLIVE',
			purple: 'ORANGE',
			yellow: 'PINK',
			blue: 'TAN',
			fuchsia: 'PURPLE',
			Cyan: 'CYAN',
		}[color] ?? 'VIOLET'
	);
};
